import { useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useDesignerMutations, useDesigners } from "../hooks";
import { DESIGNER_ROLE_LABELS, type Designer, type DesignerRole } from "../types";
import { apiErr } from "../utils";
import { PageHeader, TableShell, Th } from "../components/ui";

function DesignerFormModal({
  designer,
  defaultRole,
  onClose,
}: {
  designer: Designer | null;
  defaultRole: DesignerRole;
  onClose: () => void;
}) {
  const m = useDesignerMutations();
  const { data: users } = useInternalUsers();
  const [role, setRole] = useState<DesignerRole>(designer?.role ?? defaultRole);
  const [userId, setUserId] = useState(designer?.user ?? "");
  const [title, setTitle] = useState(designer?.title ?? "");
  const [firstName, setFirstName] = useState(designer?.first_name ?? "");
  const [lastName, setLastName] = useState(designer?.last_name ?? "");
  const [license, setLicense] = useState(designer?.license_number ?? "");
  // Adres korespondencyjny — drukuje się w nagłówku oświadczeń projektanta (TOM II).
  const [street, setStreet] = useState(designer?.street ?? "");
  const [buildingNo, setBuildingNo] = useState(designer?.building_no ?? "");
  const [postalCode, setPostalCode] = useState(designer?.postal_code ?? "");
  const [city, setCity] = useState(designer?.city ?? "");
  const [isActive, setIsActive] = useState(designer?.is_active ?? true);
  const [signature, setSignature] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isCoordinator = role === "coordinator";
  const saving = m.create.isPending || m.update.isPending || m.uploadSignature.isPending;

  const submit = async () => {
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError("Imię i nazwisko są wymagane.");
      return;
    }
    const payload = {
      role,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      title: title.trim(),
      // Nr uprawnień dotyczy tylko projektantów uprawnionych.
      license_number: isCoordinator ? "" : license.trim(),
      street: street.trim(),
      building_no: buildingNo.trim(),
      postal_code: postalCode.trim(),
      city: city.trim(),
      is_active: isActive,
      user: userId || null,
    };
    try {
      const saved = designer
        ? await m.update.mutateAsync({ id: designer.id, ...payload })
        : await m.create.mutateAsync(payload);
      // Podpis (PNG) wgrywamy osobnym requestem — dopiero gdy mamy id rekordu.
      if (signature[0]) {
        await m.uploadSignature.mutateAsync({ id: saved.id, file: signature[0] });
      }
      onClose();
    } catch (e) {
      setError(apiErr(e));
    }
  };

  return (
    <Modal open onClose={onClose} title={designer ? "Edytuj wpis" : "Nowy wpis"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Kategoria</span>
          <select
            className="input-base mt-1"
            value={role}
            onChange={(e) => setRole(e.target.value as DesignerRole)}
          >
            {(Object.keys(DESIGNER_ROLE_LABELS) as DesignerRole[]).map((r) => (
              <option key={r} value={r}>{DESIGNER_ROLE_LABELS[r]}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="section-label">Tytuł</span>
            <input
              className="input-base mt-1"
              placeholder="np. mgr inż."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          {!isCoordinator && (
            <label className="block">
              <span className="section-label">Nr uprawnień</span>
              <input className="input-base mt-1" value={license} onChange={(e) => setLicense(e.target.value)} />
            </label>
          )}
          <label className="block">
            <span className="section-label">Imię *</span>
            <input className="input-base mt-1" autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">Nazwisko *</span>
            <input className="input-base mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>

        {!isCoordinator && (
          <div>
            <span className="section-label">Adres korespondencyjny</span>
            <div className="mt-1 grid grid-cols-4 gap-2">
              <input className="input-base col-span-3" placeholder="Ulica (np. ul. Polna)"
                value={street} onChange={(e) => setStreet(e.target.value)} />
              <input className="input-base" placeholder="Nr"
                value={buildingNo} onChange={(e) => setBuildingNo(e.target.value)} />
              <input className="input-base" placeholder="00-000"
                value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              <input className="input-base col-span-3" placeholder="Miejscowość"
                value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <p className="mt-1 text-[11px] font-mono text-[var(--color-text-muted)]">
              Drukuje się w nagłówku obu oświadczeń projektanta w TOM II („Tytuły prawne
              do nieruchomości”) — oświadczenie podpisuje osoba fizyczna, więc stoi tam
              jej adres, nie adres biura.
            </p>
          </div>
        )}

        {isCoordinator && (
          <label className="block">
            <span className="section-label">Konto w systemie</span>
            <SearchableSelect
              className="mt-1"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              options={(users ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username }))}
              placeholder="— bez powiązania —"
            />
            <p className="mt-1 text-[11px] font-mono text-[var(--color-text-muted)]">
              Wiąże wpis z rejestrem pełnomocnictw — dzięki temu na porozumieniu TAURON
              drukuje się numer pełnomocnictwa tej osoby. Bez powiązania system dopasowuje
              po imieniu i nazwisku.
            </p>
          </label>
        )}

        <div>
          <span className="section-label">Podpis (PNG)</span>
          <div className="mt-1">
            <FileDropZone accept="image/png" files={signature} onFilesChange={setSignature} />
          </div>
          {designer?.signature_file && signature.length === 0 && (
            <p className="mt-1 text-[11px] font-mono text-[var(--color-text-muted)]">
              Aktualny podpis:{" "}
              {designer.signature_file.url ? (
                <a href={designer.signature_file.url} target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline">
                  {designer.signature_file.original_filename}
                </a>
              ) : (
                designer.signature_file.original_filename
              )}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span className="section-label">Aktywny</span>
        </label>

        {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button size="sm" disabled={!firstName.trim() || !lastName.trim()} loading={saving} onClick={submit}>
            {designer ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const ROLE_FILTERS: { value: DesignerRole | "all"; label: string }[] = [
  { value: "all", label: "Wszyscy" },
  { value: "licensed_designer", label: "Projektanci uprawnieni" },
  { value: "coordinator", label: "Opracowujący" },
];

export function DesignersPage() {
  const { data, isLoading } = useDesigners();
  const m = useDesignerMutations();
  const [editing, setEditing] = useState<Designer | null>(null);
  const [creating, setCreating] = useState<DesignerRole | null>(null);
  const [roleFilter, setRoleFilter] = useState<DesignerRole | "all">("all");

  const rows = (data ?? []).filter((d) => roleFilter === "all" || d.role === roleFilter);
  // Domyślna kategoria nowego wpisu = aktywny filtr (gdy „Wszyscy", nowy projektant).
  const newRole: DesignerRole = roleFilter === "coordinator" ? "coordinator" : "licensed_designer";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Projektanci i opracowujący"
        subtitle="Słownik podpisów do formatek — projektanci uprawnieni oraz opracowujący z ramienia ELEKTIM (imię, tytuł, nr uprawnień, podpis) · DLC_01"
        actions={<Button size="sm" onClick={() => setCreating(newRole)}>+ Nowy wpis</Button>}
      />


      <div className="flex gap-1.5">
        {ROLE_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setRoleFilter(f.value)}
            className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wide rounded border transition-colors ${
              roleFilter === f.value
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-[var(--color-border-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Kategoria</Th>
              <Th>Tytuł</Th>
              <Th>Imię i nazwisko</Th>
              <Th>Nr uprawnień</Th>
              <Th>Podpis</Th>
              <Th>Status</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
                <td className="px-3 py-2.5 text-[11px] font-mono uppercase tracking-wide text-[var(--color-text-secondary)]">
                  {DESIGNER_ROLE_LABELS[d.role]}
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">{d.title || "—"}</td>
                <td className="px-3 py-2.5 font-medium text-[var(--color-text-primary)]">{d.full_name}</td>
                <td className="px-3 py-2.5 font-mono text-[13px]">{d.license_number || "—"}</td>
                <td className="px-3 py-2.5">
                  {d.signature_file ? (
                    d.signature_file.url ? (
                      <a href={d.signature_file.url} target="_blank" rel="noreferrer" className="text-[var(--color-primary)] hover:underline text-xs font-mono">
                        podpis
                      </a>
                    ) : (
                      <span className="text-xs font-mono">podpis</span>
                    )
                  ) : (
                    <span className="section-label">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {d.is_active ? (
                    <span className="text-[11px] font-mono text-[var(--color-text-secondary)]">aktywny</span>
                  ) : (
                    <span className="text-[11px] font-mono text-[var(--color-text-muted)]">nieaktywny</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(d)}>Edytuj</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.confirm(`Usunąć projektanta „${d.display_name}"?`) && m.remove.mutate(d.id)}
                    >
                      Usuń
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center section-label tracking-wide">
                  Brak wpisów. Dodaj pierwszy wpis słownika.
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <DesignerFormModal
          designer={editing}
          defaultRole={editing?.role ?? creating ?? "licensed_designer"}
          onClose={() => { setCreating(null); setEditing(null); }}
        />
      )}
    </div>
  );
}
